import { useEffect, useRef, useState } from 'react';
import { authApi, type EstadoAuth } from '../api/authApi';
import { useDialogos } from '@/shared/ui/DialogosProvider';

type Props = {
  estado: EstadoAuth;
  onAbrirMaquinas: () => void;
  onAbrirLicenca: () => void;
  onLogout: () => Promise<void>;
};

function inicial(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export function MenuUsuario({ estado, onAbrirMaquinas, onAbrirLicenca, onLogout }: Props) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dialogos = useDialogos();

  useEffect(() => {
    function fechar(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAberto(false);
      }
    }
    if (aberto) document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);

  if (!estado.conta) return null;
  const conta = estado.conta;

  async function sair() {
    setAberto(false);
    const ok = await dialogos.confirmar({
      titulo: 'Sair da conta',
      mensagem: 'Deseja realmente sair? Você precisará entrar de novo na próxima abertura.',
      textoConfirmar: 'Sair',
    });
    if (!ok) return;
    try {
      await authApi.logout();
    } catch {
      // Mesmo se falhar a chamada no servidor, a sessão local é limpa.
    }
    await onLogout();
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: '1px solid var(--color-border)',
          borderRadius: 999,
          padding: '4px 12px 4px 4px',
          cursor: 'pointer',
        }}
        aria-haspopup="menu"
        aria-expanded={aberto}
      >
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: '#1e40af',
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {inicial(conta.nomeEmpresa)}
        </span>
        {/* Nome e plano saem da topbar no celular — não cabem ao lado do
            menu, do tema e do indicador de conexão, e empurrariam a barra
            para fora da tela. Continuam visíveis dentro do menu aberto. */}
        <span className="menu-usuario__identificacao">
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
            {conta.nomeEmpresa}
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{conta.plano}</span>
        </span>
      </button>

      {aberto && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 220,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-md)',
            padding: 4,
            zIndex: 50,
          }}
        >
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>logado como</div>
            <div style={{ fontSize: 13, color: 'var(--color-text)', wordBreak: 'break-all' }}>
              {conta.email}
            </div>
          </div>
          <button type="button" style={itemStyle} onClick={() => { setAberto(false); onAbrirMaquinas(); }}>
            Minhas máquinas
          </button>
          <button type="button" style={itemStyle} onClick={() => { setAberto(false); onAbrirLicenca(); }}>
            Minha assinatura
          </button>
          <button type="button" style={{ ...itemStyle, color: 'var(--color-danger)' }} onClick={sair}>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

const itemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 13,
  borderRadius: 6,
  color: 'var(--color-text)',
};
